/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { formatCurrency, getErrorMessage } from '@sportninja/common/utils/utils'
import {
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react'
import Icon from 'src/components/Icon'
import LoadingSpinner from 'src/components/LoadingSpinner'
import { EventCard } from 'src/components/RegistrationEventCard'
import { EventProgress } from 'src/components/RegistrationEventProgress'
import { RegistrationContext } from '../context/RegistrationContext'
import {
  PageTitle,
  REGISTRATION_STEPS,
  alternateButtonStyle,
  buttonStyle,
  headingButtonStyle,
  headingSixStyle,
  paragraphMediumBoldStyle,
  paragraphMediumStyle,
  paragraphSmallBoldStyle,
  paragraphSmallStyle,
} from './Main'
import { Alert, App, Spin } from 'antd'
import Text from 'src/components/ts-components/Text'
import { checkIfUserIsVerified } from './New_ConfirmYourEmail'
import { registerError } from 'src/utils/sentry'
import { weightSemiBold } from 'src/components/css'
import usePaymentProvider, {
  PAYMENT_PROVIDERS,
  PAYMENT_STATUS,
} from 'src/hooks/usePaymentProvider'
import CreditCardInput from 'src/components/CreditCardInput'

// Hardcoded variable to switch between payment providers
// Set to PAYMENT_PROVIDERS.SPORTS_PAY to use SportsPay, or PAYMENT_PROVIDERS.STRIPE to use Stripe
// const ACTIVE_PAYMENT_PROVIDER = PAYMENT_PROVIDERS.SPORTS_PAY

const RegistrationPayment = () => {
  const {
    session,
    setStep,
    registration,
    updateSessionVerifyEmail,
    deleteSession,
    currency,
    couponInfo,
    division,
    role,
    teamOfficialType,
    isTeamRep,
    updateSessionConfirmSubmission,
    updateSessionConfirmSportsPaySubmission,
    countries,
  } = useContext(RegistrationContext)

  const ACTIVE_PAYMENT_PROVIDER =
    registration?.registration?.payment_provider_type?.id

  const { message } = App.useApp()
  const waiverRequired = registration?.registration?.show_waiver

  // Local state for error handling
  const [localError, setLocalError] = useState(null)

  // State for credit card input values (for SportsPay)
  const [creditCardValues, setCreditCardValues] = useState({
    cardNumber: '',
    expiryDate: '',
    cvc: '',
    country: '',
    cardType: '',
    errors: {},
  })

  // Ref to track if element has been mounted
  const elementMounted = useRef(false)

  const {
    total,
    tax,
    subtotal,
    client_secret,
    // This is used by sports pay to dinamically load the script
    host_name,
    root_schedule_name,
    connected_account_id,
  } = session

  const deposit = useMemo(() => {
    if (couponInfo?.id) {
      return formatCurrency(
        isTeamRep ? division?.deposit : division?.player_deposit,
        currency?.name
      )
    }
    return formatCurrency(subtotal, currency?.name)
  }, [
    couponInfo?.id,
    subtotal,
    currency?.name,
    isTeamRep,
    division?.deposit,
    division?.player_deposit,
  ])

  /**
   * Handles the payment success flow with the following steps:
   * 1. Verifies the user's email
   * 2. Checks if user is verified
   * 3. Confirms submission and redirects to review page
   * 4. Handles any errors that occur during the process
   * @returns {Promise<boolean>} True if payment was successful, false otherwise
   */
  const handlePaymentSuccess = async ({ sportsPayToken }) => {
    try {
      // If this is sports pay, just return for now
      if (ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.SPORTS_PAY) {
        try {
          // Step 4a: Confirm the submission
          await updateSessionConfirmSportsPaySubmission(sportsPayToken)

          // Step 5a: Wait before redirecting to review page
          await new Promise((resolve) => setTimeout(resolve, 2000))
          setStep(REGISTRATION_STEPS.REGISTER_REVIEW)
          return true
        } catch (submissionError) {
          setPaymentStatus(PAYMENT_STATUS.FAILED)
          handlePaymentError(submissionError)
          return false
        }
      }

      // Step 1: Verify the user's email
      await updateSessionVerifyEmail()

      try {
        // Step 2: Check if user is verified
        await checkIfUserIsVerified()

        // Step 3a: User is verified - wait before confirming submission
        await new Promise((resolve) => setTimeout(resolve, 15000))

        try {
          // Step 4a: Confirm the submission
          await updateSessionConfirmSubmission()

          // Step 5a: Wait before redirecting to review page
          await new Promise((resolve) => setTimeout(resolve, 2000))
          setStep(REGISTRATION_STEPS.REGISTER_REVIEW)
        } catch (submissionError) {
          // Handle submission confirmation error
          const errorMessage = getErrorMessage(submissionError)
          setLocalError(`${errorMessage} - Please Contact SportNinja Support.`)
          registerError(submissionError, 'Error confirming submission', {
            session,
            division,
            couponInfo,
            teamOfficialType,
            role,
          })
        }
      } catch (verificationError) {
        // Step 3b: User is not verified, but payment was successful
        message.open({
          type: 'success',
          content: 'Payment Successful.',
          duration: 6,
        })

        // Step 4b: Wait before confirming submission
        await new Promise((resolve) => setTimeout(resolve, 20000))

        try {
          // Step 5b: Confirm the submission
          await updateSessionConfirmSubmission()
          setStep(REGISTRATION_STEPS.REGISTER_REVIEW)
        } catch (submissionError) {
          // Handle submission confirmation error
          setLocalError(
            `We were unable to confirm your submission. Please Contact SportNinja Support. ${getErrorMessage(
              submissionError
            )}`
          )
          registerError(submissionError, 'Error confirming submission', {
            session,
            division,
            couponInfo,
            teamOfficialType,
            role,
          })
        }
      }

      return true
    } catch (sessionError) {
      // Handle session errors
      if (sessionError?.status === 410) {
        setLocalError('Session Has Expired. Please Try Again.')

        // Wait before reloading the page
        await new Promise((resolve) => setTimeout(resolve, 1500))
        deleteSession()
        window.location.reload()
      } else {
        const errorMessage = getErrorMessage(sessionError)
        setLocalError(errorMessage)
      }

      return false
    }
  }

  // Handle payment errors
  const handlePaymentError = useCallback(
    (err) => {
      console.error('Payment error:', err)

      // Get a user-friendly error message
      let errorMessage = getErrorMessage(err)

      // Handle specific error types
      if (err?.type === 'card_error') {
        // Card errors are user-facing errors that occur during payment processing
        errorMessage =
          err.message ||
          'Your card was declined. Please try another payment method.'
      } else if (err?.type === 'validation_error') {
        // Validation errors occur when the card details are invalid
        errorMessage =
          err.message || 'Please check your card details and try again.'
      } else if (
        err?.type === 'api_error' ||
        err?.type === 'api_connection_error'
      ) {
        // API errors are usually temporary issues with the payment service
        errorMessage =
          'We are experiencing issues with our payment service. Please try again later.'
      } else {
        // For any other errors, provide a generic message
        errorMessage =
          errorMessage ||
          'An error occurred while processing your payment. Please try again.'
      }

      // Set the error message to display to the user
      setLocalError(errorMessage)

      // Log the error to Sentry for tracking
      registerError(err, 'Payment processing error', {
        session,
        division,
        couponInfo,
        teamOfficialType,
        role,
      })
    },
    [session, division, couponInfo, teamOfficialType, role]
  )

  // Format credit card data for SportsPay
  const formatSportsPayCardData = useCallback(() => {
    if (!creditCardValues) return null

    // Extract month and year from expiry date (MM / YY format)
    const expiryParts = creditCardValues.expiryDate.split(' / ')
    const month = expiryParts[0]
    const year = expiryParts[1] ? `20${expiryParts[1]}` : ''

    return {
      CARDNUM: creditCardValues.cardNumber.replace(/\s+/g, ''),
      CARDEXPMM: month,
      CARDEXPYYYY: year,
      CARDCVV: creditCardValues.cvc,
    }
  }, [creditCardValues])

  // Initialize payment provider hook
  const {
    paymentStatus,
    setPaymentStatus,
    isLoading,
    error: paymentError,
    processPayment,
    resetPayment,
    mountStripeElement,
    validateSportsPayCard,
    isInitialized,
  } = usePaymentProvider({
    provider: ACTIVE_PAYMENT_PROVIDER,
    clientSecret: client_secret,
    sportsPayApiKey: client_secret,
    connectAccount: connected_account_id,
    sportsPayHost: host_name,
    onPaymentSuccess: handlePaymentSuccess,
    onPaymentError: handlePaymentError,
    sessionData: {
      session,
      division,
      couponInfo,
      teamOfficialType,
      role,
    },
  })

  // Set error if payment information is missing
  useEffect(() => {
    if (
      ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.STRIPE &&
      (!client_secret || !connected_account_id)
    ) {
      setLocalError(
        !client_secret
          ? 'Payment information is missing. Please try again or contact support.'
          : 'Payment processing account is missing. Please try again or contact support.'
      )
    }
  }, [client_secret, connected_account_id])

  // Mount Stripe element when form is ready
  useEffect(() => {
    // Only mount the element if using Stripe
    if (ACTIVE_PAYMENT_PROVIDER !== PAYMENT_PROVIDERS.STRIPE) {
      return
    }

    // Only mount the element once
    if (elementMounted.current) {
      return
    }
    if (!isInitialized) {
      return
    }
    const paymentDiv = document.querySelector('#payment-element')
    if (paymentDiv && client_secret && connected_account_id) {
      mountStripeElement('payment-element')
      elementMounted.current = true
    }
  }, [client_secret, connected_account_id, mountStripeElement, isInitialized])

  // Validate SportsPay card data when it changes
  useEffect(() => {
    if (
      ACTIVE_PAYMENT_PROVIDER !== PAYMENT_PROVIDERS.SPORTS_PAY ||
      !validateSportsPayCard ||
      !isInitialized
    ) {
      return
    }

    // If there are errors, don't validate
    if (
      creditCardValues?.errors?.cardNumber ||
      creditCardValues?.errors?.expiryDate ||
      creditCardValues?.errors?.cvc
    ) {
      return
    }

    // Only validate if we have enough data
    if (
      creditCardValues.cardNumber &&
      creditCardValues.expiryDate &&
      creditCardValues.cvc &&
      creditCardValues.cvc.length > 3
    ) {
      const cardData = formatSportsPayCardData()
      if (cardData) {
        const validationResult = validateSportsPayCard(cardData)
        if (!validationResult.datavalid) {
          if (!validationResult.cardnum.isvalid) {
            if (validationResult.cardnum.message) {
              setLocalError(validationResult.cardnum.message)
            }
          }
          if (!validationResult.cardexp.isvalid) {
            if (validationResult.cardexp.message) {
              setLocalError(validationResult.cardexp.message)
            }
          }
          if (!validationResult.cardcvv.isvalid) {
            if (validationResult.cardcvv.message) {
              setLocalError(validationResult.cardcvv.message)
            }
          }
          return
        }
      }
    }
  }, [
    creditCardValues,
    validateSportsPayCard,
    isInitialized,
    formatSportsPayCardData,
  ])

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    // Clear any previous errors before processing payment
    setLocalError(null)

    if (ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.SPORTS_PAY) {
      // Check for credit card validation errors
      if (creditCardValues.errors?.expiryDate) {
        setLocalError(creditCardValues.errors.expiryDate)
        return
      }

      // Format card data for SportsPay
      const cardData = formatSportsPayCardData()
      if (!cardData) {
        setLocalError('Please enter valid card details')
        return
      }

      await processPayment(cardData)
    } else {
      // Stripe payment processing
      await processPayment()
    }
  }

  // Handle credit card input changes
  const handleCreditCardChange = (values) => {
    setCreditCardValues(values)
  }

  // Determine which error to display (local or from payment provider)
  const displayError = localError || paymentError

  return (
    <>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          flex: 1;
          justify-content: center;
          background-color: ${colors.NEUTRAL_800};
          width: 1080px;
          padding: 0 35px 35px 35px;
          border-radius: 16px 16px 0 0;
          @media (max-width: 768px) {
            min-width: 0;
            width: 100%;
          }
        `}
      >
        {/* {renderEmailSection()} */}
        <EventCard eventInfo={registration} />
        {/*  Progress */}
        <EventProgress
          eventProgress={5}
          isWaiverRequired={waiverRequired}
          isTeamRep={isTeamRep}
        />
        <PageTitle
          title={
            division?.single_payment
              ? 'Pay Your Registration'
              : 'Pay Your Deposit'
          }
          subtitle={
            division?.single_payment
              ? 'Please make a payment to complete your registration for the competition.'
              : 'Please make a deposit payment to complete your registration for the competition.'
          }
          showAlert={true}
          showSuccess={false}
        />
        <div
          css={css`
            max-height: 334px;
            display: flex;
            justify-content: center;
            align-items: flex-start;
            gap: 24px;
            align-self: stretch;
            /* change the flex direction on mobile */
            @media (max-width: 768px) {
              flex-direction: column;
              gap: 0px;
              align-items: center;
              max-height: unset;
            }
            /* Remove max-height constraint when using SportsPay */
            ${ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.SPORTS_PAY &&
            `
              max-height: unset;
            `}
          `}
        >
          {/* Payment Info - SportNinja Data */}
          <div
            css={css`
              display: flex;
              /* padding: 16px 0px; */
              flex-direction: column;
              align-items: center;
              gap: 16px;
              flex: 1 0 0;
              @media (max-width: 768px) {
                margin-bottom: 16px;
                width: 100%;
              }
            `}
          >
            <p
              css={css`
                ${paragraphMediumBoldStyle}
                color: ${colors.WHITE};
                align-self: stretch;
              `}
            >
              Payment Info
            </p>
            {division?.deposit && division?.deposit > 0 ? (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 16px;
                  align-self: stretch;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: flex-start;
                    flex: 1 0 0;
                  `}
                >
                  <p
                    css={css`
                      ${paragraphSmallStyle}
                      color: ${colors.WHITE};
                    `}
                  >
                    Deposit Fee
                  </p>
                  <p
                    css={css`
                      ${paragraphMediumBoldStyle}
                      color: ${colors.WHITE};
                    `}
                  >
                    {root_schedule_name}
                  </p>
                </div>
                <p
                  css={css`
                    ${paragraphMediumStyle}
                    color: ${colors.WHITE};
                  `}
                >
                  {deposit}
                </p>
              </div>
            ) : null}
            {/* Separator */}
            <div
              css={css`
                width: 100%;
                border-color: ${colors.VERY_DARK_GRAYISH_BLUE};
                border-bottom-width: 1px;
                border-bottom-style: solid;
              `}
            />
            <div
              css={css`
                display: flex;
                align-items: flex-start;
                gap: 16px;
                align-self: stretch;
              `}
            >
              <p
                css={css`
                  ${paragraphMediumStyle}
                  color: ${colors.WHITE};
                  flex: 1 0 0;
                `}
              >
                Subtotal
              </p>
              <p
                css={css`
                  ${paragraphMediumStyle}
                  color: ${colors.WHITE};
                `}
              >
                {formatCurrency(subtotal, currency?.name)}
              </p>
            </div>
            {couponInfo?.id && division?.single_payment ? (
              <div
                css={css`
                  display: flex;
                  align-items: flex-start;
                  gap: 16px;
                  align-self: stretch;
                `}
              >
                <p
                  css={css`
                    ${paragraphMediumStyle}
                    ${weightSemiBold}
                    color: ${colors.SUCCESS_100};
                    flex: 1 0 0;
                  `}
                >
                  Coupon Code: {couponInfo?.code}
                </p>
                <p
                  css={css`
                    ${paragraphMediumStyle}
                    ${weightSemiBold}
                    color: ${colors.SUCCESS_100};
                  `}
                >
                  -{formatCurrency(couponInfo?.discount_amount, currency?.name)}
                </p>
              </div>
            ) : null}
            <div
              css={css`
                display: flex;
                align-items: flex-start;
                gap: 16px;
                align-self: stretch;
              `}
            >
              <p
                css={css`
                  ${paragraphMediumStyle}
                  color: ${colors.WHITE};
                  flex: 1 0 0;
                `}
              >
                Total Tax
              </p>
              <p
                css={css`
                  ${paragraphMediumStyle}
                  color: ${colors.WHITE};
                `}
              >
                {formatCurrency(tax, currency?.name)}
              </p>
            </div>

            {/* Separator */}
            <div
              css={css`
                width: 100%;
                border-color: ${colors.VERY_DARK_GRAYISH_BLUE};
                border-bottom-width: 1px;
                border-bottom-style: solid;
              `}
            />
            <div
              css={css`
                display: flex;
                align-items: flex-start;
                gap: 22px;
                align-self: stretch;
              `}
            >
              <p
                css={css`
                  ${headingButtonStyle}
                  color: ${colors.WHITE};
                  flex: 1 0 0;
                `}
              >
                Total
              </p>
              <p
                css={css`
                  ${headingButtonStyle}
                  color: ${colors.WHITE};
                `}
              >
                {formatCurrency(total, currency?.name)}
              </p>
            </div>
            {division?.single_payment ? null : (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                  gap: 4px;
                  align-self: stretch;
                  /* display none if couponInfo is null */
                  display: ${couponInfo?.id ? 'flex' : 'none'};
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: flex-start;
                    gap: 16px;
                    align-self: stretch;
                  `}
                >
                  <p
                    css={css`
                      ${paragraphMediumStyle}
                      ${weightSemiBold}
                      color: ${colors.SUCCESS_100};
                      flex: 1 0 0;
                    `}
                  >
                    Coupon Code: {couponInfo?.code}
                  </p>
                  <p
                    css={css`
                      ${paragraphMediumStyle}
                      ${weightSemiBold}
                      color: ${colors.SUCCESS_100};
                    `}
                  >
                    -
                    {formatCurrency(
                      couponInfo?.discount_amount,
                      currency?.name
                    )}
                  </p>
                </div>
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  * Discount will be applied to the remaining balance.
                </Text>
              </div>
            )}
          </div>
          {/* Separator Between SportNinja and Stripe Stuff */}
          <div
            css={css`
              height: 100%;
              width: 1px;
              background-color: ${colors.VERY_DARK_GRAYISH_BLUE};
            `}
          />
          {/* Payment Provider UI */}
          <div
            css={css`
              /* padding: 16px 0px; */
              width: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-direction: column;
              @media (max-width: 768px) {
                width: 100%;
                padding-top: 0px;
              }
            `}
          >
            <div
              css={css`
                width: 100%;
              `}
            >
              {paymentStatus === PAYMENT_STATUS.SUCCESS ? (
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 250px;
                  `}
                >
                  <Icon name='check' color={colors.PRIMARY} fontSize={24} />
                  <p
                    css={css`
                      ${headingButtonStyle}
                      color: ${colors.WHITE};
                      margin-top: 32px;
                    `}
                  >
                    Payment Successful. <br />
                    You will be redirected shortly.
                  </p>
                  <Spin
                    css={css`
                      margin-top: 32px;
                      color: ${colors.PRIMARY};
                    `}
                  />
                </div>
              ) : paymentStatus === PAYMENT_STATUS.FAILED ? (
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 300px;
                    width: 100%;
                    overflow: hidden;
                  `}
                >
                  <Icon name='times' color={colors.ERROR_LIGHT} fontSize={24} />
                  <p
                    css={css`
                      ${headingButtonStyle}
                      color: ${colors.WHITE};
                      margin-top: 32px;
                      text-align: center;
                      width: 100%;
                      overflow-wrap: break-word;
                      word-wrap: break-word;
                      word-break: break-word;
                    `}
                  >
                    Payment Failed
                  </p>
                  <span
                    css={css`
                      ${paragraphSmallBoldStyle}
                      color: ${colors.WHITE};
                      margin-top: 16px;
                      margin-bottom: 16px;
                      text-align: center;
                      max-width: 90%;
                      overflow-wrap: break-word;
                      word-wrap: break-word;
                      word-break: break-word;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    `}
                  >
                    {displayError || 'Something went wrong'}
                  </span>
                  <div
                    css={css`
                      display: flex;
                      flex-direction: row;
                      justify-content: center;
                      align-items: center;
                      gap: 16px;
                      margin-top: 16px;
                      @media (max-width: 768px) {
                        flex-direction: column-reverse;
                        width: 100%;
                        gap: 8px;
                      }
                    `}
                  >
                    <button
                      type='button'
                      disabled={isLoading}
                      onClick={async () => {
                        if (
                          // eslint-disable-next-line no-alert
                          window.confirm('Are you sure you want to go back?')
                        ) {
                          if (waiverRequired && !isTeamRep) {
                            setStep(REGISTRATION_STEPS.WAIVER)
                          } else {
                            setStep(REGISTRATION_STEPS.TEAM_REGISTER)
                          }
                        }
                      }}
                      css={css`
                        ${alternateButtonStyle}
                        ${headingSixStyle}
                          width: 100%;
                        max-height: 40px;
                        cursor: ${isLoading ? 'not-allowed' : 'pointer'};
                      `}
                    >
                      Back
                    </button>
                    <button
                      type='button'
                      disabled={isLoading}
                      onClick={() => {
                        resetPayment()
                        // Reset the element mounted flag so it can be mounted again
                        elementMounted.current = false
                        // Clear any error messages
                        setLocalError(null)

                        // Force remount of Stripe element by adding a small delay
                        if (
                          ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.STRIPE
                        ) {
                          setTimeout(() => {
                            const paymentDiv =
                              document.querySelector('#payment-element')
                            if (
                              paymentDiv &&
                              client_secret &&
                              connected_account_id &&
                              isInitialized
                            ) {
                              mountStripeElement('payment-element')
                              elementMounted.current = true
                            }
                          }, 100)
                        }
                      }}
                      css={css`
                        ${buttonStyle}
                        ${headingSixStyle}
                      width: 100%;
                        max-height: 40px;
                        cursor: ${isLoading ? 'not-allowed' : 'pointer'};
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                      `}
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Show our custom CreditCardInput for SportsPay */}
                  {ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.SPORTS_PAY && (
                    <CreditCardInput
                      onChange={handleCreditCardChange}
                      countries={countries}
                    />
                  )}

                  <div>
                    <form id='payment-form' onSubmit={handleSubmit}>
                      <div id='card-element' />
                      {/* Only show Stripe payment element if using Stripe */}
                      {ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.STRIPE && (
                        <div id='payment-element' />
                      )}
                      <div
                        css={css`
                          display: flex;
                          flex-direction: row;
                          justify-content: center;
                          align-items: center;
                          gap: 16px;
                          margin-top: 16px;
                          @media (max-width: 768px) {
                            flex-direction: column-reverse;
                            width: 100%;
                            gap: 8px;
                          }
                        `}
                      >
                        <button
                          type='button'
                          disabled={isLoading}
                          onClick={async () => {
                            if (
                              // eslint-disable-next-line no-alert
                              window.confirm(
                                'Are you sure you want to go back?'
                              )
                            ) {
                              if (waiverRequired && !isTeamRep) {
                                setStep(REGISTRATION_STEPS.WAIVER)
                              } else {
                                setStep(REGISTRATION_STEPS.TEAM_REGISTER)
                              }
                            }
                          }}
                          css={css`
                            ${alternateButtonStyle}
                            ${headingSixStyle}
                          width: 100%;
                            max-height: 40px;
                            cursor: ${isLoading ? 'not-allowed' : 'pointer'};
                          `}
                        >
                          Back
                        </button>
                        <button
                          disabled={isLoading}
                          type='submit'
                          css={css`
                            ${buttonStyle}
                            ${headingSixStyle}
                          width: 100%;
                            max-height: 40px;
                            cursor: ${isLoading ? 'not-allowed' : 'pointer'};
                          `}
                        >
                          {isLoading ? <LoadingSpinner size={1} /> : 'PAY NOW'}
                        </button>
                      </div>
                    </form>
                  </div>
                  <div
                    css={css`
                      visibility: ${'hidden'};
                    `}
                  >
                    <LoadingSpinner />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        {(displayError || paymentStatus === PAYMENT_STATUS.FAILED) && (
          <Alert
            message={displayError}
            type='error'
            showIcon
            css={css`
              margin-top: 16px;
            `}
          />
        )}

        {/* Display which payment provider is active (for testing) */}
        <div
          css={css`
            margin-top: ${ACTIVE_PAYMENT_PROVIDER ===
            PAYMENT_PROVIDERS.SPORTS_PAY
              ? '32px'
              : '16px'};
            margin-bottom: 16px;
            text-align: center;
            color: ${colors.NEUTRAL_100};
            font-size: 12px;
            position: relative;
            z-index: 1;
            display: none;
          `}
        >
          Payment Provider: {ACTIVE_PAYMENT_PROVIDER}
        </div>
      </div>
    </>
  )
}

export default RegistrationPayment
